import React from 'react';
import { useScheduleUpcomingQuery, flattenScheduleUpcomingToEvents } from '../queries';
import { UPCOMING_EVENTS_LIST_SIZE } from '../api';
import { liveWindowFromResults, liveUntilSecFromEventLike, isVideoReadyFromResults } from 'features/realtime/liveFromResults';
import { useServerNow } from 'shared/queries/serverTime';
import { useOngoingEventQuery } from 'features/video/queries';

export type NextEventForSchedule = {
  nextEvent: any | null;
  eventId: number | string | null;
  eventTimestamp: number | null;
  endTimestamp: number | null;
  countdownSeconds: number | null;
  isLive: boolean;
  /** Ongoing payload from GET /schedules/:id/live (null when no ongoing). */
  ongoingPayload: Record<string, unknown> | null;
  refetchOngoing: () => void;
  /** Events that have not ended yet (nowSec < liveUntil). Used for selection sync. */
  notEndedEvents: any[];
};

/**
 * Insert live event into list in timestamp order: after events with event_timestamp <= liveTs, before events with greater.
 */
function insertLiveInOrder(
  list: any[],
  livePayload: Record<string, unknown>,
  scheduleId: string | number
): any[] {
  const ongoingId = String(livePayload.event_id ?? '');
  if (list.some((e: any) => String(e?.event_id ?? '') === ongoingId)) return list;
  const ts = livePayload.event_timestamp;
  if (ts == null || !Number.isFinite(Number(ts))) return list;
  const liveTs = Number(ts);
  const asEvent = { ...livePayload, schedule_id: scheduleId } as any;
  const idx = list.findIndex((e: any) => e?.event_timestamp != null && Number(e.event_timestamp) > liveTs);
  if (idx < 0) return [...list, asEvent];
  return [...list.slice(0, idx), asEvent, ...list.slice(idx)];
}

/** Per-event liveUntil: from event or from ongoingPayload when event_id matches. */
function getLiveUntilSec(
  e: any,
  ongoingPayload: Record<string, unknown> | null
): number | null {
  const fromLike = liveUntilSecFromEventLike(e);
  if (fromLike != null && Number.isFinite(fromLike)) return fromLike;
  if (ongoingPayload != null && String(ongoingPayload.event_id) === String(e?.event_id)) {
    const window = liveWindowFromResults(ongoingPayload as any);
    return window?.liveUntilSec ?? null;
  }
  return null;
}

/**
 * Single source of truth for "next event" and countdown for a schedule.
 * Uses schedule upcoming (upcoming only) + GET /live for ongoing; inserts live event in list in timestamp order.
 * @param sportId - Optional; when provided, isLive requires VOD to be ready (isVideoReadyFromResults).
 */
export function useNextEventForSchedule(
  scheduleId: string | number | null | undefined,
  sportId?: string | number | null
): NextEventForSchedule {
  const nowMs = useServerNow();
  const nowSec = Math.floor(Number(nowMs || 0) / 1000);

  const { data: scheduleUpcomingRaw, refetch: refetchScheduleUpcoming } = useScheduleUpcomingQuery({
    scheduleId: scheduleId ?? undefined,
    numEvents: UPCOMING_EVENTS_LIST_SIZE,
  });

  const ongoingQuery = useOngoingEventQuery({
    scheduleId: scheduleId ?? undefined,
    enabled: scheduleId != null,
  });

  const ongoingPayloadRaw = React.useMemo(() => {
    const data = ongoingQuery.data;
    if (scheduleId == null || !data || typeof data !== 'object' || data.event_id == null) return null;
    return data as Record<string, unknown>;
  }, [scheduleId, ongoingQuery.data]);

  const upcomingList = React.useMemo(() => {
    const flat = flattenScheduleUpcomingToEvents(scheduleUpcomingRaw);
    const list = Array.isArray(flat) ? [...flat] : [];
    if (ongoingPayloadRaw != null && scheduleId != null) {
      return insertLiveInOrder(list, ongoingPayloadRaw, scheduleId);
    }
    return list;
  }, [scheduleUpcomingRaw, scheduleId, ongoingPayloadRaw]);

  const nextEventResult = React.useMemo(() => {
    const list = upcomingList;
    if (!list.length) return { event: null, notEndedEvents: [] };

    const filteredList = list.filter((e: any) => {
      const liveUntil = getLiveUntilSec(e, ongoingPayloadRaw);
      return liveUntil == null || !Number.isFinite(liveUntil) || nowSec < liveUntil;
    });

    const firstCurrentlyLive = filteredList.find((e: any) => {
      const start = e?.event_timestamp != null ? Number(e.event_timestamp) : null;
      const liveUntil = getLiveUntilSec(e, ongoingPayloadRaw);
      return (
        start != null &&
        Number.isFinite(start) &&
        liveUntil != null &&
        Number.isFinite(liveUntil) &&
        nowSec >= start &&
        nowSec < liveUntil
      );
    });
    if (firstCurrentlyLive) return { event: firstCurrentlyLive, notEndedEvents: filteredList };

    const firstStartedButNotEnded = filteredList.find((e: any) => {
      const start = e?.event_timestamp != null ? Number(e.event_timestamp) : null;
      const liveUntil = getLiveUntilSec(e, ongoingPayloadRaw);
      if (start == null || !Number.isFinite(start) || nowSec < start) return false;
      if (liveUntil == null || !Number.isFinite(liveUntil)) return true;
      return nowSec < liveUntil;
    });
    if (firstStartedButNotEnded) return { event: firstStartedButNotEnded, notEndedEvents: filteredList };

    const firstFuture = filteredList.find((e: any) => {
      const ts = e?.event_timestamp != null ? Number(e.event_timestamp) : null;
      return ts != null && Number.isFinite(ts) && ts > nowSec;
    });
    if (firstFuture) return { event: firstFuture, notEndedEvents: filteredList };

    const firstNotEnded = filteredList.find((e: any) => {
      const liveUntil = getLiveUntilSec(e, ongoingPayloadRaw);
      if (liveUntil != null && Number.isFinite(liveUntil)) return nowSec < liveUntil;
      const ts = e?.event_timestamp != null ? Number(e.event_timestamp) : null;
      return ts != null && Number.isFinite(ts) && ts >= nowSec;
    });
    const event = firstNotEnded ?? filteredList[0] ?? null;
    return { event, notEndedEvents: filteredList };
  }, [upcomingList, nowSec, ongoingPayloadRaw]);

  const nextEvent = nextEventResult.event;
  const notEndedEvents = nextEventResult.notEndedEvents;

  const eventId = nextEvent?.event_id != null ? nextEvent.event_id : null;
  const eventTimestamp =
    nextEvent?.event_timestamp != null && Number.isFinite(Number(nextEvent.event_timestamp))
      ? Number(nextEvent.event_timestamp)
      : null;
  const endTimestamp = React.useMemo(() => {
    if (!nextEvent) return null;
    const fromLike = liveUntilSecFromEventLike(nextEvent);
    let end = fromLike != null && Number.isFinite(fromLike) ? fromLike : null;
    if (end == null) {
      const endTs = nextEvent?.end_timestamp != null ? Number(nextEvent.end_timestamp) : null;
      if (endTs != null && Number.isFinite(endTs)) end = endTs;
    }
    if (end == null) {
      const ts = nextEvent?.event_timestamp != null ? Number(nextEvent.event_timestamp) : null;
      const dur = nextEvent?.duration != null ? Number(nextEvent.duration) : null;
      if (ts != null && dur != null && Number.isFinite(ts) && Number.isFinite(dur)) end = ts + dur;
    }
    if (end == null && ongoingPayloadRaw != null && String(ongoingPayloadRaw.event_id) === String(nextEvent?.event_id)) {
      const window = liveWindowFromResults(ongoingPayloadRaw as any);
      if (window?.liveUntilSec != null && Number.isFinite(window.liveUntilSec)) end = window.liveUntilSec;
    }
    return end;
  }, [nextEvent, ongoingPayloadRaw]);

  const liveWindow = ongoingPayloadRaw ? liveWindowFromResults(ongoingPayloadRaw as any) : null;
  const isLive =
    Boolean(ongoingPayloadRaw) &&
    Boolean(liveWindow) &&
    liveWindow?.startSec != null &&
    Number.isFinite(liveWindow.startSec) &&
    liveWindow?.liveUntilSec != null &&
    Number.isFinite(liveWindow.liveUntilSec) &&
    nowSec >= liveWindow.startSec &&
    nowSec < liveWindow.liveUntilSec &&
    (sportId == null || isVideoReadyFromResults(ongoingPayloadRaw, sportId));

  /** Expose ongoing only when still in live window; null once event has ended so UI/video stop using it. */
  const ongoingPayload =
    ongoingPayloadRaw &&
    liveWindow &&
    liveWindow.liveUntilSec != null &&
    Number.isFinite(liveWindow.liveUntilSec) &&
    nowSec < liveWindow.liveUntilSec
      ? ongoingPayloadRaw
      : null;

  const countdownSeconds = React.useMemo(() => {
    if (isLive) return 0;
    if (eventTimestamp == null || !Number.isFinite(eventTimestamp)) return null;
    return Math.max(0, eventTimestamp - nowSec);
  }, [isLive, eventTimestamp, nowSec]);

  const refetchOngoing = React.useCallback(() => {
    refetchScheduleUpcoming();
    ongoingQuery.refetch();
  }, [refetchScheduleUpcoming, ongoingQuery.refetch]);

  return {
    nextEvent,
    eventId,
    eventTimestamp,
    endTimestamp,
    countdownSeconds,
    isLive,
    ongoingPayload,
    refetchOngoing,
    notEndedEvents,
  };
}
